import React, { useContext } from "react";
import Container from "@src/components/Container";
import { Button, theme, Space } from "@src/components/nessie-web";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import styled from "@emotion/styled";
import { mediaQueries } from "@src/styles/theme";
import { logEvent } from "@src/utils/logClient";
import curveSVG from "@src/assets/images/schools/cta-curve.svg";
import { ExternalSwitches } from "@src/utils/experiments/constants";
import { useCreateConvertExperimentEvent } from "@src/utils/experiment";

const {
  colors: { dt_white },
} = theme;

const CTASectionContainer = styled.section<{ bgImage: string }>`
  background-image: url(${curveSVG}), linear-gradient(0deg, #3a2a50 -0.01%, rgba(55, 47, 74, 0) 100%),
    url("${(props) => props.bgImage}");
  background-size: auto 24px, cover, cover;
  background-repeat: no-repeat;
  background-position: top center, center, center;
  ${mediaQueries[0]} {
    background-size: 102% auto, cover, cover;
  }
`;

const CTAContent = styled.div`
  width: 100%;
  min-height: 420px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueries[0]} {
    min-height: 600px;
  }
`;

const CTATile = styled.div``;

const CTATitle = styled.h2`
  font-weight: 800;
  font-size: 28px;
  line-height: 36px;
  text-align: center;
  letter-spacing: -0.2px;
  margin: 0;
  color: ${dt_white};

  ${mediaQueries[0]} {
    font-size: 44px;
    line-height: 48px;
    letter-spacing: -0.4px;
  }
`;

const CTAButtons = styled.div`
  display: grid;
  gap: 12px;
  align-items: center;
  position: unset;
  width: 100%;

  ${mediaQueries[0]} {
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    justify-content: center;
  }
`;

type CTASectionProps = {
  cta_title: string;
  cta_button_text: string;
  cta_button_url?: string;
  cta_button_contact_text: string;
  cta_button_contact_url?: string;
  cta_background_image_url: any;
  button_log_event_name?: string;
  params?: any;
};

const RedesignCTASection: React.FC<CTASectionProps> = ({
  cta_title,
  cta_button_text,
  cta_button_url,
  cta_button_contact_text,
  cta_button_contact_url,
  cta_background_image_url,
  button_log_event_name,
  params,
}) => {
  const t = useContext(TranslationContext);
  const convertExperiment = useCreateConvertExperimentEvent(ExternalSwitches.SCHOOLS_PAGE);
  function fireLogEvent() {
    convertExperiment();
    logEvent({
      eventName: "web.external.cds.bottom_main_cta.click",
      eventValue: location.href,
      metadata: params,
      experiments: [ExternalSwitches.SCHOOLS_PAGE],
    });
  }

  return (
    <CTASectionContainer bgImage={cta_background_image_url}>
      <Container>
        <CTAContent>
          <CTATile>
            <CTATitle>{t.translate(cta_title)}</CTATitle>
            <Space size="l" />
            <CTAButtons>
              <Button href={cta_button_url} onClick={fireLogEvent}>
                {t.translate(cta_button_text)}
              </Button>
              <Button
                href={cta_button_contact_url}
                kind="secondary"
                onClick={logEvent({
                  eventName: "web.external.cds.bottom_secondary_cta.click",
                  eventValue: location.href,
                  metadata: params,
                  experiments: [ExternalSwitches.SCHOOLS_PAGE],
                })}
              >
                {t.translate(cta_button_contact_text)}
              </Button>
            </CTAButtons>
          </CTATile>
        </CTAContent>
      </Container>
    </CTASectionContainer>
  );
};

export default RedesignCTASection;
